/**
 * FOLLOW & FAVORITES CONTEXT
 * ──────────────────────────────────────────────────────────────
 * Lets any user (mentor or mentee) follow mentors and mark them
 * as favourites. State is persisted to localStorage so it
 * survives page refreshes.
 *
 * In production replace localStorage with:
 *   POST /api/follows          → follow a mentor
 *   DELETE /api/follows/:id    → unfollow
 *   PUT  /api/favorites/:id    → toggle favourite
 *   GET  /api/follows          → list followed mentors
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

const FollowContext = createContext(null);

const STORAGE_KEY_FOLLOWS   = 'db_follows';
const STORAGE_KEY_FAVORITES = 'db_favorites';

const load = (key, fallback = []) => {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : fallback; } catch { return fallback; }
};
const save = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

export function FollowProvider({ children }) {
  // Arrays of mentor IDs (numbers)
  const [followed,  setFollowed]  = useState(() => load(STORAGE_KEY_FOLLOWS,   []));
  const [favorites, setFavorites] = useState(() => load(STORAGE_KEY_FAVORITES, []));

  // Follower counts — keyed by mentor id, start with mock base counts
  const [followerCounts, setFollowerCounts] = useState({
    1: 1842, 2: 3210, 3: 924, 4: 1503, 5: 720, 6: 2100, 7: 680, 8: 1290, 9: 410,
  });

  const isFollowing  = useCallback((mentorId) => followed.includes(mentorId),   [followed]);
  const isFavorite   = useCallback((mentorId) => favorites.includes(mentorId),  [favorites]);

  const toggleFollow = useCallback((mentorId) => {
    setFollowed(prev => {
      const next = prev.includes(mentorId)
        ? prev.filter(id => id !== mentorId)
        : [...prev, mentorId];
      save(STORAGE_KEY_FOLLOWS, next);
      return next;
    });
    // update live follower count
    setFollowerCounts(prev => ({
      ...prev,
      [mentorId]: (prev[mentorId] || 0) + (followed.includes(mentorId) ? -1 : 1),
    }));
  }, [followed]);

  const toggleFavorite = useCallback((mentorId) => {
    setFavorites(prev => {
      const next = prev.includes(mentorId)
        ? prev.filter(id => id !== mentorId)
        : [...prev, mentorId];
      save(STORAGE_KEY_FAVORITES, next);
      return next;
    });
  }, []);

  const getFollowerCount = useCallback((mentorId) => followerCounts[mentorId] || 0, [followerCounts]);

  return (
    <FollowContext.Provider value={{
      followed, favorites,
      isFollowing, isFavorite,
      toggleFollow, toggleFavorite,
      getFollowerCount,
    }}>
      {children}
    </FollowContext.Provider>
  );
}

export function useFollow() {
  const ctx = useContext(FollowContext);
  if (!ctx) throw new Error('useFollow must be inside FollowProvider');
  return ctx;
}
